'use client'

import { ComponentType, ReactNode, useContext, useEffect } from 'react'
import { useMatch } from 'react-router-dom'

import {
  useConversationContext,
  ConversationListContext,
} from '@marketplace-web/messaging/conversation-providers-feature'
import { Conversation } from '@marketplace-web/messaging/conversation-messaging-data'
import { CONVERSATION_URL } from '@marketplace-web/messaging/navigation-feature'
import { BrazeConversation } from '@marketplace-web/messaging/conversation-type-braze-feature'
import { selfServiceViewScreenEvent } from '@marketplace-web/messaging/tracking-data'
import { useAbTest, useTrackAbTestCallback } from '@marketplace-web/feature-flags/ab-tests-data'
import { useBreakpoint } from '@marketplace-web/breakpoints/breakpoints-feature'
import { useConversationOrderEmailTracking } from '@marketplace-web/order-emails/conversation-order-emails-tracking-feature'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { UiState } from '@marketplace-web/shared/ui-state-util'
import { useBrazeInboxMessageCards } from '@marketplace-web/braze/braze-feature'
import { ContentLoader } from '@marketplace-web/common-components/content-loader-ui'
import { HttpStatus } from '@marketplace-web/core-api/api-client-util'
import { transformBrazeInboxMessageCardDto } from '@marketplace-web/braze/braze-data'
import { useFeatureSwitch } from '@marketplace-web/feature-flags/feature-switches-data'
import { ErrorBoundary } from '@marketplace-web/error-display/error-display-util'

import { useHandleConversationSSE } from '../../hooks/useHandleConversationSSE'

import { isBrazeConversation } from '../../utils'
import LegacyConversation from '../LegacyConversation'
import LoadingFailedEmptyState from '../LoadingFailedEmptyState'
import NotFoundEmptyState from '../NotFoundEmptyState'
import TrackMessagingViewEvent from '../TrackMessagingViewEvent'
import ConversationHeaderLeftAction from '../ConversationHeader/ConversationHeaderLeftAction'

type UseTrackSSEInConversationExposeProps = {
  conversationId: number | string | null | undefined
}

function useTrackSSEInConversationExpose({ conversationId }: UseTrackSSEInConversationExposeProps) {
  const trackExpose = useTrackAbTestCallback()
  const sseInConversationAbTest = useAbTest('sse_in_conversation')

  useEffect(() => {
    if (!conversationId) return

    trackExpose(sseInConversationAbTest)
  }, [trackExpose, sseInConversationAbTest, conversationId])
}

function usePortablesScrollToTop() {
  const breakpoints = useBreakpoint()

  useEffect(() => {
    if (!breakpoints.portables) return

    window.scroll({ top: 0, left: 0 })
  }, [breakpoints])
}

type UseMarkConversationAsReadProps = {
  conversationId: number | string | null | undefined
}

function useMarkConversationAsRead({ conversationId }: UseMarkConversationAsReadProps) {
  const { conversations, markAsRead } = useContext(ConversationListContext)

  useEffect(() => {
    if (!conversationId) return

    const conversationInList = conversations.find(conv => conversationId === conv.id)

    if (!conversationInList || isBrazeConversation(conversationInList)) return

    if ('unread' in conversationInList && !conversationInList.unread) return

    markAsRead({ id: conversationId })
  }, [conversations, conversationId, markAsRead])
}

type UseTrackViewScreenInConversationProps = {
  transactionId: number | string | null | undefined
}

function useTrackMessageReplyViewScreen({ transactionId }: UseTrackViewScreenInConversationProps) {
  const { track } = useTracking()

  useEffect(() => {
    if (!transactionId) return

    track(selfServiceViewScreenEvent({ screen: 'message_reply', transactionId }))
  }, [track, transactionId])
}

type UseConversationFromUrlProps = {
  uiState: UiState
  conversationId: string | number | undefined
  getConversationRequest: (params: { conversationId: string }) => void
  setConversation: (conversation: Conversation) => void
}

function useConversationFromUrl({
  uiState,
  conversationId,
  getConversationRequest,
  setConversation,
}: UseConversationFromUrlProps) {
  const { inboxMessageCards } = useBrazeInboxMessageCards()
  const conversationRouteMatch = useMatch(CONVERSATION_URL(':id'))

  useEffect(() => {
    if (uiState !== UiState.Idle) return
    if (!conversationRouteMatch?.params.id) return
    if (!inboxMessageCards) return

    const isConversationIdMismatched = String(conversationId) !== conversationRouteMatch.params.id

    if (!isConversationIdMismatched) return

    const brazeConversationCard = inboxMessageCards.find(
      card => card.id === conversationRouteMatch.params.id,
    )

    if (brazeConversationCard) {
      setConversation(transformBrazeInboxMessageCardDto(brazeConversationCard))

      return
    }

    getConversationRequest({ conversationId: conversationRouteMatch.params.id })
  }, [
    uiState,
    getConversationRequest,
    setConversation,
    conversationRouteMatch,
    conversationId,
    inboxMessageCards,
  ])
}
type LegacyConversationUiStateWrapperProps = {
  children: ({ conversationId }: { conversationId: string }) => ReactNode
}

const LegacyConversationUiStateWrapper = ({ children }: LegacyConversationUiStateWrapperProps) => {
  const { inboxMessageCards } = useBrazeInboxMessageCards()

  const { conversation, conversationUiState, conversationRequestStatus, transactionUiState } =
    useConversationContext()
  const isWebMessagingSvcFSEnabled = useFeatureSwitch('web_messaging_svc')

  const isLoading =
    !conversation &&
    (!inboxMessageCards || [UiState.Pending, UiState.Idle].includes(conversationUiState))

  const hasError = !conversation && conversationUiState === UiState.Failure
  const isConversationNotFound = hasError && conversationRequestStatus === HttpStatus.NotFound

  const isTransactionLoading =
    conversation?.transactionId &&
    transactionUiState !== UiState.Success &&
    !isWebMessagingSvcFSEnabled

  if (isLoading) return <ContentLoader testId="conversation-page-loader" />
  if (isConversationNotFound) return <NotFoundEmptyState />
  if (hasError) return <LoadingFailedEmptyState />
  if (isTransactionLoading) return <ContentLoader testId="conversation-page-loader" />

  return (
    <ErrorBoundary FallbackComponent={() => <LoadingFailedEmptyState />}>
      {children({ conversationId: String(conversation!.id) })}
    </ErrorBoundary>
  )
}

type ConversationTypeComponent = ComponentType<{
  conversationId: string
  leftHeaderAction: ReactNode
}>

const CONVERSATION_TYPE_COMPONENT_MAP = {
  legacy: LegacyConversation,
  braze: BrazeConversation,
  default: LegacyConversation,
} satisfies Partial<Record<Conversation['conversationType'], ConversationTypeComponent>> & {
  default: ConversationTypeComponent
}

const ConversationComponent = () => {
  const { conversation, conversationUiState, getConversationRequest, setConversation } =
    useConversationContext()

  useConversationFromUrl({
    getConversationRequest,
    setConversation,
    conversationId: conversation?.id,
    uiState: conversationUiState,
  })
  useConversationOrderEmailTracking({ transactionId: conversation?.transactionId })
  usePortablesScrollToTop()
  useHandleConversationSSE()
  useMarkConversationAsRead({ conversationId: conversation?.id })
  useTrackSSEInConversationExpose({ conversationId: conversation?.id })
  useTrackMessageReplyViewScreen({ transactionId: conversation?.transactionId })

  const ConversationTypeComponent: ConversationTypeComponent =
    (conversation?.conversationType &&
      CONVERSATION_TYPE_COMPONENT_MAP[conversation.conversationType]) ||
    CONVERSATION_TYPE_COMPONENT_MAP.default

  return (
    <LegacyConversationUiStateWrapper>
      {({ conversationId }) => (
        <>
          <TrackMessagingViewEvent screen="conversation" />
          <ConversationTypeComponent
            conversationId={conversationId}
            leftHeaderAction={<ConversationHeaderLeftAction />}
          />
        </>
      )}
    </LegacyConversationUiStateWrapper>
  )
}

export default ConversationComponent
